import {
  createContext,
  useContext,
  useMemo,
  type Context,
} from 'react';

import type { AssetIconResolver, AssetImageResolver } from '../components/base/BaseAssetIcon';
import type { BaseAppearance } from '../components/base/types';
import type { DataExplorerAppearance } from '../components/data-explorer/types';
import {
  defaultMonkeysLocale,
  type MonkeysLocaleMessages,
  type MonkeysResolvedLocale,
} from '../locale';
import type { CompiledThemeTokens } from '../theme-system';

export type MonkeysDirection = 'ltr' | 'rtl';

export interface MonkeysEnvironment {
  getDocument?: () => Document | null;
  getWindow?: () => Window | null;
}

export type MonkeysPortalContainer =
  | Element
  | null
  | (() => Element | null);

export interface MonkeysPortalOptions {
  container?: MonkeysPortalContainer;
  disabled?: boolean;
}

export type MonkeysLoadingStateVariant = 'spinner' | 'skeleton';
export type MonkeysEmptyStateVariant = 'default' | 'compact';
export type MonkeysStatusStateDensity = 'compact' | 'default' | 'comfortable';

export type MonkeysListFooterPaginationMode = 'auto' | 'pages' | 'compact';
export type MonkeysListFooterAlignment = 'start' | 'end' | 'space-between';

export interface MonkeysListFooterConfig {
  showTotal?: boolean;
  showPageSize?: boolean;
  pageSizeOptions?: number[];
  paginationMode?: MonkeysListFooterPaginationMode;
  alignment?: MonkeysListFooterAlignment;
  density?: MonkeysStatusStateDensity;
}

export interface MonkeysStatusStatesConfig {
  loading?: { variant?: MonkeysLoadingStateVariant };
  empty?: { variant?: MonkeysEmptyStateVariant };
  error?: { showRetry?: boolean };
  permission?: { showRequestAccess?: boolean };
  density?: MonkeysStatusStateDensity;
}

export interface MonkeysComponentAttributes {
  dir?: MonkeysDirection;
  'data-monkeys-direction'?: MonkeysDirection;
  'data-monkeys-locale'?: string;
  'data-monkeys-theme'?: string;
  'data-monkeys-theme-mode'?: string;
}

interface MonkeysLocaleContextValue extends MonkeysResolvedLocale {
  provided: boolean;
}

interface MonkeysThemeContextValue {
  provided: boolean;
  resolvedTheme?: CompiledThemeTokens;
  themeId?: string;
  baseAppearance?: BaseAppearance;
  dataExplorerAppearance?: DataExplorerAppearance;
  statusStates?: MonkeysStatusStatesConfig;
  listFooter?: MonkeysListFooterConfig;
}

interface MonkeysDirectionContextValue {
  provided: boolean;
  direction?: MonkeysDirection;
}

interface MonkeysEnvironmentContextValue {
  provided: boolean;
  environment: MonkeysEnvironment;
  portal: MonkeysPortalOptions;
  iconResolver?: AssetIconResolver;
  imageResolver?: AssetImageResolver;
}

const EMPTY_ENVIRONMENT = Object.freeze({}) satisfies MonkeysEnvironment;
const EMPTY_PORTAL = Object.freeze({}) satisfies MonkeysPortalOptions;

interface MonkeysProviderContexts {
  locale: Context<MonkeysLocaleContextValue>;
  theme: Context<MonkeysThemeContextValue>;
  direction: Context<MonkeysDirectionContextValue>;
  environment: Context<MonkeysEnvironmentContextValue>;
}

// Every bundled public entry contains this module. Keep only the Context
// identities in a realm-wide registry so root, provider, and component
// subpaths interoperate in both CJS and ESM builds. Provider values remain in
// React trees, so SSR requests are isolated. Bump the contract key only when
// these Context value shapes become intentionally incompatible.
const MONKEYS_PROVIDER_CONTEXTS_KEY = Symbol.for(
  '@inf-monkeys-tech/monkeys-design/provider-context@1',
);

type MonkeysProviderContextHost = typeof globalThis & {
  [MONKEYS_PROVIDER_CONTEXTS_KEY]?: MonkeysProviderContexts;
};

function createMonkeysProviderContexts(): MonkeysProviderContexts {
  return Object.freeze({
    locale: createContext<MonkeysLocaleContextValue>({
      ...defaultMonkeysLocale,
      provided: false,
    }),
    theme: createContext<MonkeysThemeContextValue>({
      provided: false,
    }),
    direction: createContext<MonkeysDirectionContextValue>({
      provided: false,
    }),
    environment: createContext<MonkeysEnvironmentContextValue>({
      provided: false,
      environment: EMPTY_ENVIRONMENT,
      portal: EMPTY_PORTAL,
    }),
  });
}

function getMonkeysProviderContexts(): MonkeysProviderContexts {
  const host = globalThis as MonkeysProviderContextHost;
  const existing = host[MONKEYS_PROVIDER_CONTEXTS_KEY];
  if (existing) return existing;

  const contexts = createMonkeysProviderContexts();
  Object.defineProperty(host, MONKEYS_PROVIDER_CONTEXTS_KEY, {
    value: contexts,
    enumerable: false,
    configurable: false,
    writable: false,
  });
  return contexts;
}

const providerContexts = getMonkeysProviderContexts();

export const MonkeysLocaleContext = providerContexts.locale;

export const MonkeysThemeContext = providerContexts.theme;

export const MonkeysDirectionContext = providerContexts.direction;

export const MonkeysEnvironmentContext = providerContexts.environment;

function mergeAppearance<T extends { slots?: object; styles?: object }>(
  inherited: T | undefined,
  explicit: T | undefined,
): T | undefined {
  if (explicit === undefined) return inherited;
  if (inherited === undefined) return explicit;

  return {
    ...inherited,
    ...explicit,
    slots:
      inherited.slots || explicit.slots
        ? { ...inherited.slots, ...explicit.slots }
        : undefined,
    styles:
      inherited.styles || explicit.styles
        ? { ...inherited.styles, ...explicit.styles }
        : undefined,
  };
}

export function useMonkeysLocale() {
  try {
    const context = useContext(MonkeysLocaleContext);
    return context ?? { ...defaultMonkeysLocale, provided: false };
  } catch {
    return { ...defaultMonkeysLocale, provided: false };
  }
}

export function useMonkeysLocaleMessages(): MonkeysLocaleMessages {
  return useContext(MonkeysLocaleContext).messages;
}

export function useMonkeysResolvedTheme() {
  return useContext(MonkeysThemeContext).resolvedTheme;
}

export function useMonkeysBaseAppearance(
  explicit?: BaseAppearance,
): BaseAppearance | undefined {
  const inherited = useContext(MonkeysThemeContext).baseAppearance;
  return useMemo(
    () => mergeAppearance(inherited, explicit),
    [explicit, inherited],
  );
}

export function useMonkeysDataExplorerAppearance(
  explicit?: DataExplorerAppearance,
): DataExplorerAppearance | undefined {
  const inherited = useContext(MonkeysThemeContext).dataExplorerAppearance;
  return useMemo(
    () => mergeAppearance(inherited, explicit),
    [explicit, inherited],
  );
}

export function useMonkeysStatusStates(): MonkeysStatusStatesConfig {
  return useContext(MonkeysThemeContext).statusStates ?? {};
}

export function useMonkeysListFooter(): MonkeysListFooterConfig {
  return useContext(MonkeysThemeContext).listFooter ?? {};
}

export function useMonkeysDirection() {
  return useContext(MonkeysDirectionContext).direction;
}

export function useMonkeysEnvironment() {
  return useContext(MonkeysEnvironmentContext).environment;
}

export function useMonkeysPortalOptions() {
  return useContext(MonkeysEnvironmentContext).portal;
}

export function useMonkeysComponentAttributes(): MonkeysComponentAttributes {
  const locale = useContext(MonkeysLocaleContext);
  const theme = useContext(MonkeysThemeContext);
  const direction = useContext(MonkeysDirectionContext);
  const providerActive = locale.provided || theme.provided || direction.provided;

  return useMemo(
    () =>
      providerActive
        ? {
            dir: direction.direction,
            'data-monkeys-direction': direction.direction,
            'data-monkeys-locale': locale.locale,
            'data-monkeys-theme': theme.themeId,
            'data-monkeys-theme-mode': theme.resolvedTheme?.colorMode,
          }
        : {},
    [
      direction.direction,
      locale.locale,
      providerActive,
      theme.resolvedTheme?.colorMode,
      theme.themeId,
    ],
  );
}

export function useMonkeysAssetIconResolver(): AssetIconResolver | undefined {
  return useContext(MonkeysEnvironmentContext).iconResolver;
}
export function useMonkeysAssetImageResolver(): AssetImageResolver | undefined {
  return useContext(MonkeysEnvironmentContext).imageResolver;
}
